import axios, { AxiosError, AxiosInstance } from 'axios';

/**
 * "You have this permission — just not here."
 *
 * Permissions resolve through the active context, and a user may hold different
 * grants in different ones. Until the backend gained `common::authz`, a refusal on
 * that basis was indistinguishable from having no access at all: the API returned a
 * bare 401 (which `sessionExpiry` reads as a lapsed session, so the user was told to
 * sign in again) or a hand-rolled 403 whose body differed per endpoint, so nothing
 * generic could interpret it.
 *
 * It now returns one shape, and this module turns it into an offer to switch rather
 * than a dead end. That matters most on the multi-division MSP shape, where sitting
 * in the wrong context is routine rather than exceptional.
 *
 * **The request is still rejected.** Unlike `sessionExpiry`, nothing is parked or
 * replayed here: switching context is a decision with consequences for the whole UI,
 * not a transparent recovery. If the user accepts, `useActivateContext` invalidates
 * every query and the failed request re-fires on its own.
 */

export interface GrantingContext {
  id: string;
  name: string;
  divisionId: string | null;
}

export interface WrongContextEvent {
  /** The permission that was refused, e.g. `device:read`. */
  action: string;
  /** Contexts of the user's own that would allow it. Never empty. */
  grantingContexts: GrantingContext[];
  /** The backend's sentence, already naming the contexts. */
  description: string;
}

interface AuthzErrorBody {
  error?: string;
  description?: string;
  errors?: {
    action?: string;
    grantingContexts?: GrantingContext[];
  };
}

type Listener = (event: WrongContextEvent) => void;

const listeners = new Set<Listener>();

/** Subscribe to wrong-context denials. Returns an unsubscribe function. */
export function onWrongContext(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function parse(error: AxiosError): WrongContextEvent | null {
  if (error.response?.status !== 403) return null;
  const body = error.response.data as AuthzErrorBody | undefined;
  if (body?.error !== 'wrong_context') return null;

  const grantingContexts = body.errors?.grantingContexts ?? [];
  // `wrong_context` is only sent when there is somewhere to switch to, but a
  // prompt offering nothing would be worse than no prompt — so require it here
  // rather than trusting the invariant across a network boundary.
  if (grantingContexts.length === 0) return null;

  return {
    action: body.errors?.action ?? '',
    grantingContexts,
    description: body.description ?? '',
  };
}

/**
 * Install on an axios instance. Idempotent per instance is *not* guaranteed —
 * call once, at startup, alongside the other interceptors in `main.tsx`.
 */
export function installWrongContextInterceptor(client: AxiosInstance = axios): () => void {
  const id = client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const event = parse(error);
      if (event) {
        for (const listener of listeners) listener(event);
      }
      return Promise.reject(error);
    },
  );

  return () => {
    client.interceptors.response.eject(id);
  };
}
